import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDownload,
  FiFileText,
  FiRefreshCw,
  FiUsers,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const REPORT_TYPES = [
  { value: "SUMMARY", label: "Employee Monthly Summary" },
  { value: "ATTENDANCE", label: "Attendance" },
  { value: "LEAVE", label: "Leave" },
  { value: "PAYROLL", label: "Payroll" },
  { value: "PAYSLIPS", label: "Payslips" },
];

function number(value) {
  return Number(value || 0);
}

function money(value) {
  return `₹${number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function minutes(value) {
  const total = Math.max(0, Math.round(number(value)));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function date(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function dateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));
}

function todayMonth() {
  return new Date().toISOString().slice(0, 7);
}

function statusClass(status) {
  const styles = {
    DRAFT: "bg-yellow-50 text-yellow-700",
    POSTED: "bg-blue-50 text-blue-700",
    PAID: "bg-green-50 text-green-700",
    PRESENT: "bg-green-50 text-green-700",
    LATE: "bg-orange-50 text-orange-700",
    HALF_DAY: "bg-amber-50 text-amber-700",
    ABSENT: "bg-red-50 text-red-700",
    ON_LEAVE: "bg-violet-50 text-violet-700",
    WEEKLY_OFF: "bg-slate-100 text-slate-700",
    HOLIDAY: "bg-cyan-50 text-cyan-700",
    NOT_YET_DUE: "bg-gray-100 text-gray-500",
  };
  return styles[status] || "bg-gray-100 text-gray-700";
}

function Status({ children, value }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
        value,
      )}`}
    >
      {children || String(value || "").replaceAll("_", " ")}
    </span>
  );
}

function buildQuery(filters) {
  const params = new URLSearchParams();
  if (filters.branchId) params.set("branchId", filters.branchId);
  if (filters.search.trim()) params.set("search", filters.search.trim());
  params.set("reportType", filters.reportType);

  if (filters.periodMode === "MONTH") {
    params.set("month", filters.month);
  } else {
    params.set("from", filters.from);
    params.set("to", filters.to);
  }

  return params;
}

export default function HrmsReports() {
  const { accessToken } = useAuth();
  const [branches, setBranches] = useState([]);
  const [report, setReport] = useState(null);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);
  const [detailTab, setDetailTab] = useState("ATTENDANCE");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState("");
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    branchId: "",
    search: "",
    periodMode: "MONTH",
    month: todayMonth(),
    from: "",
    to: "",
    reportType: "SUMMARY",
  });

  useEffect(() => {
    if (!accessToken) return;

    apiRequest("/branches", { accessToken })
      .then((response) => setBranches(response || []))
      .catch(() => setBranches([]));
  }, [accessToken]);

  const generate = useCallback(async () => {
    if (!accessToken) return;
    if (
      filters.periodMode === "CUSTOM" &&
      (!filters.from || !filters.to || filters.from > filters.to)
    ) {
      setError("Select a valid From and To date.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setExpandedEmployeeId(null);
      const response = await apiRequest(
        `/hrms-reports?${buildQuery(filters).toString()}`,
        { accessToken },
      );
      setReport(response);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Unable to generate the HRMS report.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, filters]);

  const exportReport = async (format) => {
    if (!accessToken) return;
    if (
      filters.periodMode === "CUSTOM" &&
      (!filters.from || !filters.to || filters.from > filters.to)
    ) {
      setError("Select a valid From and To date.");
      return;
    }

    try {
      setIsExporting(format);
      setError("");
      const params = buildQuery(filters);
      params.set("format", format);
      const response = await fetch(
        `${API_BASE_URL}/hrms-reports/export?${params.toString()}`,
        {
          credentials: "include",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || "Unable to export report.");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const fileName =
        /filename="([^"]+)"/i.exec(disposition)?.[1] ||
        `hrms-report.${format.toLowerCase()}`;
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (requestError) {
      setError(requestError.message || "Unable to export the report.");
    } finally {
      setIsExporting("");
    }
  };

  const totals = report?.totals;
  const employees = report?.employees || [];
  const selectedBranch = branches.find(
    (branch) => branch.id === filters.branchId,
  );

  const description = useMemo(() => {
    if (!report?.filters) return "Choose filters, then generate a report.";
    return `${report.filters.periodStart} to ${report.filters.periodEnd}${
      selectedBranch ? ` · ${selectedBranch.name}` : ""
    }`;
  }, [report, selectedBranch]);

  return (
    <div className="space-y-7">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-sm text-gray-500">HRMS / Reports</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            HRMS Reports
          </h1>
          <p className="mt-2 max-w-3xl text-gray-500">
            Generate branch-wise employee attendance, leave, payroll and payslip
            reports for a month or a selected date range.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => exportReport("CSV")}
            disabled={!report || Boolean(isExporting)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiDownload />
            {isExporting === "CSV" ? "Preparing…" : "Export CSV"}
          </button>
          <button
            type="button"
            onClick={() => exportReport("XLSX")}
            disabled={!report || Boolean(isExporting)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiDownload />
            {isExporting === "XLSX" ? "Preparing…" : "Export Excel"}
          </button>
        </div>
      </div>


      {error ? (
        <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" size={18} />
          <p>{error}</p>
        </div>
      ) : null}

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-2">
          <FiFileText className="text-primary-700" />
          <h2 className="font-semibold text-gray-900">Report filters</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Field label="Branch">
            <select
              value={filters.branchId}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  branchId: event.target.value,
                }))
              }
            >
              <option value="">All branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} · {branch.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Employee search">
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Employee name or code"
            />
          </Field>

          <Field label="Period">
            <select
              value={filters.periodMode}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  periodMode: event.target.value,
                }))
              }
            >
              <option value="MONTH">Month</option>
              <option value="CUSTOM">Custom dates</option>
            </select>
          </Field>

          {filters.periodMode === "MONTH" ? (
            <Field label="Month">
              <input
                type="month"
                value={filters.month}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    month: event.target.value,
                  }))
                }
              />
            </Field>
          ) : (
            <>
              <Field label="From date">
                <input
                  type="date"
                  value={filters.from}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      from: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="To date">
                <input
                  type="date"
                  value={filters.to}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      to: event.target.value,
                    }))
                  }
                />
              </Field>
            </>
          )}

          <Field label="Export report type">
            <select
              value={filters.reportType}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  reportType: event.target.value,
                }))
              }
            >
              {REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </Field>

        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={generate}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 font-semibold text-white hover:bg-primary-800 disabled:opacity-60"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Generating…" : "Generate report"}
          </button>
        </div>
      </section>

      {report ? (
        <>
          <p className="text-sm text-gray-500">{description}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Metric
              icon={<FiUsers />}
              label="Employees"
              value={totals.employees}
            />
            <Metric
              icon={<FiClock />}
              label="Total worked time"
              value={minutes(totals.workedMinutes)}
            />
            <Metric
              icon={<FiCalendar />}
              label="Attendance"
              value={`${totals.present} present · ${totals.late} late`}
            />
            <Metric
              icon={<FiCalendar />}
              label="Leave used"
              value={`${number(totals.paidLeaveDays) + number(totals.unpaidLeaveDays)} days`}
            />
            <Metric
              icon={<FiFileText />}
              label="Final payable"
              value={money(totals.netPayable)}
            />
          </div>

          <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-6 py-5">
              <h2 className="font-semibold text-gray-900">
                Employee report summary
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Expand an employee to review daily attendance, leave, payroll
                and payslip details.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="w-14 px-5 py-4" />
                    <th className="px-5 py-4">Employee</th>
                    <th className="px-5 py-4">Branch</th>
                    <th className="px-5 py-4 text-right">Worked</th>
                    <th className="px-5 py-4 text-right">Attendance</th>
                    <th className="px-5 py-4 text-right">Leave</th>
                    <th className="px-5 py-4 text-right">Before recovery</th>
                    <th className="px-5 py-4 text-right">Advance recovery</th>
                    <th className="px-5 py-4 text-right">Final payable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employees.map((row) => {
                    const expanded = expandedEmployeeId === row.employee.id;
                    return (
                      <EmployeeRows
                        key={row.employee.id}
                        row={row}
                        expanded={expanded}
                        detailTab={detailTab}
                        onToggle={() => {
                          setExpandedEmployeeId(
                            expanded ? null : row.employee.id,
                          );
                          setDetailTab("ATTENDANCE");
                        }}
                        onTab={setDetailTab}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!employees.length ? (
              <div className="px-6 py-16 text-center text-gray-500">
                No employees match these filters.
              </div>
            ) : null}
          </section>
        </>
      ) : (
        <section className="rounded-3xl border border-dashed border-gray-200 bg-white px-6 py-20 text-center">
          <FiFileText className="mx-auto text-gray-300" size={42} />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            Generate an HRMS report
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Select a branch and period, then generate the employee overview.
          </p>
        </section>
      )}
    </div>
  );
}

function EmployeeRows({ row, expanded, detailTab, onToggle, onTab }) {
  const { employee, attendance, leave, payroll } = row;
  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-5 py-5">
          <button
            type="button"
            onClick={onToggle}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-700"
            aria-label={
              expanded ? "Collapse employee details" : "Expand employee details"
            }
          >
            {expanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </td>
        <td className="px-5 py-5">
          <p className="font-semibold text-gray-900">{employee.fullName}</p>
          <p className="mt-1 text-xs text-gray-500">
            {employee.code} · {employee.designation?.name || "No designation"}
          </p>
        </td>
        <td className="px-5 py-5 text-sm text-gray-600">
          {employee.branch?.name || "Unassigned"}
        </td>
        <td className="px-5 py-5 text-right font-semibold text-gray-900">
          {minutes(attendance.workedMinutes)}
        </td>
        <td className="px-5 py-5 text-right text-sm text-gray-700">
          {attendance.present} P · {attendance.late} L · {attendance.absent} A
        </td>
        <td className="px-5 py-5 text-right text-sm text-gray-700">
          {number(leave.paidLeaveDays) + number(leave.unpaidLeaveDays)} days
        </td>
        <td className="px-5 py-5 text-right font-medium text-gray-900">
          {money(payroll.payableBeforeAdvanceRecovery)}
        </td>
        <td className="px-5 py-5 text-right font-medium text-red-600">
          {payroll.advanceRecovery ? `−${money(payroll.advanceRecovery)}` : "—"}
        </td>
        <td className="px-5 py-5 text-right font-bold text-primary-700">
          {money(payroll.netPayable)}
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={9} className="bg-gray-50 p-0">
            <EmployeeDetails row={row} tab={detailTab} onTab={onTab} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function EmployeeDetails({ row, tab, onTab }) {
  const tabs = ["ATTENDANCE", "LEAVE", "PAYROLL", "PAYSLIPS"];
  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap gap-2 border-b border-gray-200 pb-4">
        {tabs.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onTab(item)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold ${
              tab === item
                ? "bg-primary-700 text-white"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            {item === "PAYSLIPS"
              ? "Payslips"
              : item.charAt(0) + item.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {tab === "ATTENDANCE" ? <AttendanceTable daily={row.daily} /> : null}
      {tab === "LEAVE" ? <LeaveDetails leave={row.leave} /> : null}
      {tab === "PAYROLL" ? <PayrollDetails items={row.payrollDetails} /> : null}
      {tab === "PAYSLIPS" ? (
        <PayslipDetails items={row.payrollDetails} />
      ) : null}
    </div>
  );
}

function AttendanceTable({ daily }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Sessions</th>
            <th className="px-4 py-3 text-right">Worked</th>
            <th className="px-4 py-3 text-right">Late</th>
            <th className="px-4 py-3 text-right">Under-time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {daily.map((day) => (
            <tr key={day.date}>
              <td className="px-4 py-3 font-medium text-gray-900">
                {date(day.date)}
              </td>
              <td className="px-4 py-3">
                <Status value={day.status}>{day.statusLabel}</Status>
              </td>
              <td className="px-4 py-3 text-gray-600">
                {day.sessions.length
                  ? day.sessions
                      .map(
                        (session) =>
                          `${dateTime(session.checkInAt)} – ${dateTime(session.checkOutAt)}`,
                      )
                      .join(" · ")
                  : "—"}
              </td>
              <td className="px-4 py-3 text-right font-semibold">
                {minutes(day.workedMinutes)}
              </td>
              <td className="px-4 py-3 text-right">
                {minutes(day.lateMinutes)}
              </td>
              <td className="px-4 py-3 text-right">
                {minutes(day.earlyExitMinutes)}
              </td>
            </tr>
          ))}
          {!daily.length ? (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                No attendance records in this period.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function LeaveDetails({ leave }) {
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <DetailCard title="Approved leave in selected period">
        {leave.requests.map((item) => (
          <div
            key={item.id}
            className="flex justify-between gap-4 border-b border-gray-100 py-3 last:border-0"
          >
            <div>
              <p className="font-medium text-gray-900">{item.leaveType.name}</p>
              <p className="text-xs text-gray-500">
                {date(item.startDate)} – {date(item.endDate)} ·{" "}
                {item.isPaid ? "Paid" : "Unpaid"}
              </p>
            </div>
            <span className="font-semibold">{number(item.totalDays)} days</span>
          </div>
        ))}
        {!leave.requests.length ? (
          <Empty text="No approved leave in this period." />
        ) : null}
      </DetailCard>
      <DetailCard title="Leave balances">
        {leave.balances.map((item) => (
          <div
            key={`${item.year}-${item.leaveType.id}`}
            className="flex justify-between gap-4 border-b border-gray-100 py-3 last:border-0"
          >
            <div>
              <p className="font-medium text-gray-900">{item.leaveType.name}</p>
              <p className="text-xs text-gray-500">
                {item.year} · Allocated {number(item.allocatedDays)} · Used{" "}
                {number(item.usedDays)}
              </p>
            </div>
            <span className="font-semibold text-primary-700">
              {number(item.availableDays)} available
            </span>
          </div>
        ))}
        {!leave.balances.length ? (
          <Empty text="No leave balances for this year." />
        ) : null}
      </DetailCard>
    </div>
  );
}

function PayrollDetails({ items }) {
  return <PayrollTable items={items} showPayment={false} />;
}

function PayslipDetails({ items }) {
  return <PayrollTable items={items} showPayment />;
}

function PayrollTable({ items, showPayment }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-3">Period</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3 text-right">Gross</th>
            <th className="px-4 py-3 text-right">Before recovery</th>
            <th className="px-4 py-3 text-right">Advance recovery</th>
            <th className="px-4 py-3 text-right">Final payable</th>
            <th className="px-4 py-3">Status</th>
            {showPayment ? <th className="px-4 py-3">Reference</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((item) => (
            <tr key={item.id}>
              <td className="px-4 py-3">
                {date(item.periodStart)} – {date(item.periodEnd)}
              </td>
              <td className="px-4 py-3">{item.runType.replaceAll("_", " ")}</td>
              <td className="px-4 py-3 text-right">
                {money(item.grossEarnings)}
              </td>
              <td className="px-4 py-3 text-right">
                {money(item.payableBeforeAdvanceRecovery)}
              </td>
              <td className="px-4 py-3 text-right text-red-600">
                {item.advanceRecovery ? `−${money(item.advanceRecovery)}` : "—"}
              </td>
              <td className="px-4 py-3 text-right font-bold text-primary-700">
                {money(item.netPayable)}
              </td>
              <td className="px-4 py-3">
                <Status value={item.status} />
              </td>
              {showPayment ? (
                <td className="px-4 py-3 text-gray-600">
                  {item.paymentReference || "—"}
                </td>
              ) : null}
            </tr>
          ))}
          {!items.length ? (
            <tr>
              <td
                colSpan={showPayment ? 8 : 7}
                className="px-4 py-10 text-center text-gray-500"
              >
                No payroll items overlap this period.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      <span className="mb-2 block">{label}</span>
      <div className="[&>input]:w-full [&>input]:rounded-xl [&>input]:border [&>input]:border-gray-200 [&>input]:px-3 [&>input]:py-2.5 [&>select]:w-full [&>select]:rounded-xl [&>select]:border [&>select]:border-gray-200 [&>select]:px-3 [&>select]:py-2.5">
        {children}
      </div>
    </label>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{label}</p>
        <span className="rounded-xl bg-primary-50 p-2 text-primary-700">
          {icon}
        </span>
      </div>
      <p className="mt-4 text-xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function DetailCard({ title, children }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5">
      <h3 className="mb-3 font-semibold text-gray-900">{title}</h3>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return <p className="py-5 text-sm text-gray-500">{text}</p>;
}
